import Vue from 'vue';
import _ from 'lodash';
import { mapActions } from 'vuex';
import DDLogs from '@/DDLogs';
import OpenFundHeader from '@/components/meitav-dash/OpenFundHeader';
import Wizard from '@/mixins/Wizard';
import UploadSocialId from '@/views/meitav-dash/open-fund/pages/UploadSocialId';
import Gender from '@/views/meitav-dash/open-fund/pages/Gender';
import BirthInfo from '@/views/meitav-dash/open-fund/pages/BirthInfo';
import Address from '@/views/meitav-dash/open-fund/pages/Address';
import Payment from '@/views/meitav-dash/open-fund/pages/Payment';
import SocialIdNumber from '@/views/meitav-dash/open-fund/pages/SocialIdNumber';
import NameInfo from '@/views/meitav-dash/open-fund/pages/NameInfo';
import Email from '@/views/meitav-dash/open-fund/pages/Email';
import ChooseMonthlyDeposit from '@/views/meitav-dash/open-fund/pages/ChooseMonthlyDeposit';
import ChooseOneTimeDeposit from '@/views/meitav-dash/open-fund/pages/ChooseOneTimeDeposit';
import Statements from '@/views/meitav-dash/open-fund/pages/Statements';
import ChooseSocialIdType from '@/views/meitav-dash/open-fund/pages/ChooseSocialIdType';
import PreCheckout from '@/views/meitav-dash/open-fund/pages/PreCheckout';
import Support from '@/components/Support';
import TermsAndConditionsLinkButtons from '@/components/TermsAndConditionsLinkButtons';
import SubmitInProgress from '@/views/meitav-dash/open-fund/dialogs/SubmitInProgress';
import store from '@/store';
import GeneralErrorHandling from '@/mixins/GeneralErrorHandling';

const BASE_PATH = 'open-fund';

export async function redirectByFundStatus(to, from, next) {
  await store.dispatch('meitavDash/fundRequests/initFundRequests');
  const shouldRedirectToFundStatus = store.state.meitavDash.fundRequests.requestStatus
    && store.state.meitavDash.fundRequests.requestStatus !== 'created';
  if (shouldRedirectToFundStatus) {
    await next('/meitav/fund-status');
    return;
  }
  await next();
}

const pageRoutes = [
  {
    path: 'monthly',
    component: ChooseMonthlyDeposit,
  },
  {
    path: 'onetime',
    component: ChooseOneTimeDeposit,
  },
  {
    path: 'pre-checkout',
    component: PreCheckout,
  },
  {
    path: 'id-type',
    component: ChooseSocialIdType,
  },
  {
    path: 'upload-id',
    component: UploadSocialId,
  },
  {
    path: 'id-number',
    component: SocialIdNumber,
  },
  {
    path: 'name-info',
    component: NameInfo,
  },
  {
    path: 'gender',
    component: Gender,
  },
  {
    path: 'birth-info',
    component: BirthInfo,
  },
  {
    path: 'address',
    component: Address,
  },
  {
    path: 'email',
    component: Email,
  },
  {
    path: 'statements',
    component: Statements,
  },
  {
    path: 'payment',
    component: Payment,
  },
];

const pages = pageRoutes.map(p => p.path);

const openFundWizardComponent = Vue.component('OpenFundWizard', {
  template: ' <div class="r-root-page">\n'
          + '    <div>'
          + '       <open-fund-header show-shopping-cart :indicator="showIndicatorOnShoppingCart" :blink="blinkIndicator"/>'
          + '       <router-view :on-next="next" :on-complete="completed" ref="page"/>\n'
          + '    </div>'
          + '    <div class="r-footer"> '
          + '       <support />'
          + '       <terms-and-conditions-link-buttons />'
          + '    </div>'
          + '  </div>',
  components: { OpenFundHeader, Support, TermsAndConditionsLinkButtons },
  mixins: [Wizard, GeneralErrorHandling],
  data() {
    return {
      pages,
    };
  },
  async created() {
    const wizardState = await this.getState();
    await this.initCityAndStreetList(wizardState.city);
    await this.navigateToFirstPage(BASE_PATH, pages[0]);
    this.setCurrentPage(this.$router.currentRoute);
  },
  computed: {
    showIndicatorOnShoppingCart() {
      if (this.currentPage) {
        const checkoutFirstPageIndex = _.findIndex(this.pages, pageName => pageName === 'pre-checkout') + 1;
        const currentPageIndex = _.findIndex(this.pages, pageName => pageName === this.currentPage);
        return currentPageIndex >= checkoutFirstPageIndex;
      }
      return false;
    },
    blinkIndicator() {
      if (this.currentPage) {
        const checkoutFirstPageIndex = _.findIndex(this.pages, pageName => pageName === 'pre-checkout') + 1;
        const currentPageIndex = _.findIndex(this.pages, pageName => pageName === this.currentPage);
        return currentPageIndex === checkoutFirstPageIndex;
      }
      return false;
    },
  },
  methods: {
    ...mapActions('meitavDash/openFund', ['save', 'getState', 'submitRequest']),
    ...mapActions('meitavDash/fundRequests', ['refreshFundStatus']),
    ...mapActions('cityAndStreet', ['initCityAndStreetList']),
    async completed() {
      await this.save();
      const result = await this.submit();
      await this.handleSubmissionResult(result);
    },
    async handleSubmissionResult(result) {
      if (result.status === 'success') {
        await this.$router.push('/meitav/congratulations');
      } else {
        this.logErrorAndShowDialog(`Bad submission result [${result.status}] [${result.errorType}]`);
      }
    },
    async next() {
      await this.save();
      await this.goToNextPage(this.$refs.page, this.pages);
    },
    async submit() {
      return new Promise(resolve => {
        const dialog = this.$q.dialog({
          component: SubmitInProgress,
          statusMessage: '...הבקשה בבדיקה...',
          persistent: true, // we want the user to not be able to close it
          ok: false, // we want the user to not be able to close it
        });

        try {
          const CLIENT_SIDE_TIMEOUT = 60 * 1000;
          const updateProgressTask = setTimeout(() => {
            dialog.update({
              statusMessage: '...עוד כמה רגעים...',
            });
          }, CLIENT_SIDE_TIMEOUT / 3);

          const failSubmissionTask = setTimeout(() => {
            dialog.update({
              statusMessage: 'בקשה לפתיחת קופה נכשלה',
            });
            resolve({ status: 'error', errorType: 'clientTimeout' });
          }, CLIENT_SIDE_TIMEOUT);

          const submissionResult = this.submitRequest();

          submissionResult.then(async result => {
            clearTimeout(updateProgressTask);
            clearTimeout(failSubmissionTask);
            try {
              // We need to update the status, so clicking on back will forward the user to fund status pageֿ
              await this.refreshFundStatus();
              resolve(result);
            } catch (err) {
              DDLogs.error('Got error while handling submission result', { error: err });
              resolve({ status: 'error', errorType: 'serverError' });
            } finally {
              if (dialog) {
                dialog.hide();
              }
            }
          });

          submissionResult.catch(error => {
            clearTimeout(updateProgressTask);
            clearTimeout(failSubmissionTask);

            dialog.update({
              statusMessage: 'פתיחת קופת גמל נכשלה',
            });
            dialog.hide();
            DDLogs.error('Got error while handling submission result', { error });
            resolve({ status: 'error', errorType: 'serverError' });
          });
        } catch (error) {
          dialog.hide();
          DDLogs.error('Got error while handling submission result', { error });
          resolve({ status: 'error', errorType: 'clientError' });
        }
      });
    },
  },
});

export function createWizard() {
  return {
    path: BASE_PATH,
    name: 'Open Fund',
    component: openFundWizardComponent,
    async beforeEnter(to, from, next) {
      await redirectByFundStatus(to, from, next);
    },
    children: _.map(pageRoutes, route => {
      return {
        ...route,
        async beforeEnter(to, from, next) {
          await redirectByFundStatus(to, from, next);
        },
      };
    }),
  };
}
